import { ClipboardCopyButton } from '@/components/ui/clipboard/clipboard-button';
import { Avatar, Box, HStack, Spinner, Text } from '@chakra-ui/react';
import { Check, Prohibition, WarningTriangle } from 'iconoir-react';
import type { ComponentType, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';

/** Per-recipient outcome shape of the bulk recap, one row each. */
export type BulkRecapStatus = 'link' | 'sending' | 'sent' | 'skipped' | 'failed' | 'unknown';

export interface BulkRecapLineData {
  key: string;
  displayName: string;
  avatarUrl: string | null;
  status: BulkRecapStatus;
  /** Copyable invitation link (link mode success). */
  invitationLink: string | null;
  /** Translated failure reason (English backend message as fallback), or the skip reason. */
  message: string | null;
}

type ChipTone = 'green' | 'blue' | 'red' | 'gray' | 'orange';

/** Status pill visual per outcome. `sending` carries a spinner (the email left
 * the batch and its async send is in flight); `sent` a green check. */
const STATUS_META: Record<
  Exclude<BulkRecapStatus, 'link'>,
  { tone: ChipTone; icon: ComponentType<SVGProps<SVGSVGElement>> | null; labelKey: string }
> = {
  unknown: { tone: 'orange', icon: WarningTriangle, labelKey: 'bulkEmail.progress.unknown.title' },
  sending: {
    tone: 'blue',
    icon: null,
    labelKey: 'invitations.modal.channel.bulk.recap.emailSending',
  },
  sent: { tone: 'green', icon: Check, labelKey: 'invitations.modal.channel.bulk.recap.emailSent' },
  skipped: {
    tone: 'gray',
    icon: Prohibition,
    labelKey: 'invitations.modal.channel.bulk.recap.skipped',
  },
  failed: {
    tone: 'red',
    icon: WarningTriangle,
    labelKey: 'invitations.modal.channel.bulk.recap.failed',
  },
};

const TONE_STYLE: Record<ChipTone, { bg: string; color: string }> = {
  orange: { bg: 'orange.50', color: 'orange.700' },
  green: { bg: 'green.50', color: 'green.700' },
  blue: { bg: 'primary.50', color: 'primary.600' },
  red: { bg: 'red.50', color: 'red.600' },
  gray: { bg: 'bg.muted', color: 'fg.muted' },
};

/** The trailing status chip for a non-link line: a tinted rounded pill (borders
 * + background contrast only, no shadow). */
const StatusChip = ({ status }: { status: Exclude<BulkRecapStatus, 'link'> }) => {
  const { t } = useTranslation();
  const meta = STATUS_META[status];
  const style = TONE_STYLE[meta.tone];
  const Icon = meta.icon;
  return (
    <HStack gap="1" flexShrink={0} bg={style.bg} color={style.color} rounded="full" px="2" py="1">
      {status === 'sending' ? (
        <Spinner size="xs" borderWidth="1.5px" />
      ) : (
        Icon && <Icon width={11} height={11} />
      )}
      <Text fontSize="11px" fontWeight="medium" lineHeight="1">
        {t(meta.labelKey)}
      </Text>
    </HStack>
  );
};

/**
 * One recipient row of the bulk recap: the avatar + name lead; a link-mode
 * success shows the copyable invitation link, every other outcome shows a
 * tinted status chip. A failed/skipped line surfaces its reason as the subline,
 * already resolved by the caller (translated code, else the English message).
 */
export const BulkRecapLine = ({ line }: { line: BulkRecapLineData }) => {
  const { t } = useTranslation();
  const isLink = line.status === 'link' && !!line.invitationLink;
  const subline =
    (line.status === 'failed' || line.status === 'skipped' || line.status === 'unknown') &&
    line.message
      ? line.message
      : null;

  return (
    <HStack
      gap="2.5"
      borderWidth="1px"
      borderColor="cardBorder"
      rounded="lg"
      bg="panelBg"
      px="12px"
      py="2.5"
    >
      <Avatar.Root size="xs" flexShrink={0}>
        <Avatar.Fallback name={line.displayName} />
        <Avatar.Image src={line.avatarUrl ?? undefined} />
      </Avatar.Root>
      <Box flex="1" minW="0">
        <Text
          fontFamily="heading"
          fontSize="12.5px"
          fontWeight="semibold"
          color="dark"
          lineClamp={1}
        >
          {line.displayName}
        </Text>
        {isLink ? (
          <Text fontSize="11.5px" color="fg.muted" lineClamp={1}>
            {line.invitationLink}
          </Text>
        ) : (
          subline && (
            <Text fontSize="11.5px" color="fg.muted" lineClamp={1}>
              {subline}
            </Text>
          )
        )}
      </Box>
      {isLink ? (
        <ClipboardCopyButton
          value={line.invitationLink!}
          size="xs"
          variant="outline"
          label={t('invitations.modal.collab.wizard.done.copy')}
        />
      ) : (
        line.status !== 'link' && <StatusChip status={line.status} />
      )}
    </HStack>
  );
};
